/**
 * MGRS Conversion Utilities
 *
 * Wraps the `mgrs` npm package to provide lat/lng ↔ MGRS conversions,
 * UTM decomposition, display formatting, and tolerant input parsing.
 *
 * Pure JS — no React, no DOM dependencies.
 */

import { forward, toPoint } from 'mgrs';

/**
 * Convert latitude/longitude to an MGRS grid reference string.
 *
 * @param {number} lat  - Latitude in decimal degrees (-80 to 84)
 * @param {number} lng  - Longitude in decimal degrees (-180 to 180)
 * @param {number} [precision=5] - Grid precision:
 *   1 = 10km, 2 = 1km, 3 = 100m, 4 = 10m, 5 = 1m
 * @returns {string} MGRS string, e.g. "15TWK8500042000"
 */
export function latLngToMgrs(lat, lng, precision = 5) {
  // mgrs.forward takes [longitude, latitude] — note the order
  return forward([lng, lat], precision);
}

/**
 * Convert an MGRS string to latitude/longitude.
 *
 * @param {string} mgrsString - MGRS grid reference, e.g. "15TWK8542"
 * @returns {{ lat: number, lng: number }}
 */
export function mgrsToLatLng(mgrsString) {
  // toPoint returns [lng, lat] — center of the MGRS grid cell
  const point = toPoint(mgrsString);
  return { lat: point[1], lng: point[0] };
}

/**
 * Convert latitude/longitude to UTM components.
 *
 * Derives zone, band letter, easting, and northing from the MGRS string.
 *
 * @param {number} lat - Latitude in decimal degrees
 * @param {number} lng - Longitude in decimal degrees
 * @returns {{ zone: number, letter: string, easting: number, northing: number }}
 */
export function latLngToUtm(lat, lng) {
  const mgrs = latLngToMgrs(lat, lng, 5);
  const parsed = decomposeMgrs(mgrs);

  return {
    zone: parsed.zone,
    letter: parsed.letter,
    easting: parsed.easting,
    northing: parsed.northing,
  };
}

/**
 * Format an MGRS string with spaces for human readability.
 *
 * Input:  "15TWK8542"
 * Output: "15T WK 85 42"
 *
 * @param {string} mgrsString - Compact MGRS string
 * @returns {string} Formatted string with spaces
 */
export function formatMgrsDisplay(mgrsString) {
  const { gzd, squareId, eastingStr, northingStr } = decomposeMgrsRaw(mgrsString);
  if (!eastingStr && !northingStr) {
    return `${gzd} ${squareId}`.trim();
  }
  return `${gzd} ${squareId} ${eastingStr} ${northingStr}`.trim();
}

/**
 * Tolerant parser that accepts various MGRS input formats.
 *
 * Handles spaces, extra whitespace, mixed case. Returns a compact
 * uppercase MGRS string suitable for passing to the mgrs library.
 *
 * @param {string} input - User-typed MGRS string
 * @returns {string} Normalized compact MGRS string
 */
export function parseMgrsInput(input) {
  // Uppercase, collapse all whitespace, trim
  return input.toUpperCase().replace(/\s+/g, '').trim();
}

// ─── Internal helpers ──────────────────────────────────────────────

/**
 * Decompose an MGRS string into its raw string components.
 *
 * @param {string} mgrs - Compact MGRS string like "15TWK8542"
 * @returns {{ gzd: string, squareId: string, eastingStr: string, northingStr: string }}
 */
function decomposeMgrsRaw(mgrs) {
  // MGRS format: <zone_number><band_letter><column_letter><row_letter><easting_digits><northing_digits>
  // Zone number: 1-60 (1-2 digits)
  // Band letter: C-X (excluding I, O) — 1 char
  // Column + Row letters: 2 chars
  // Easting + Northing digits: 0, 2, 4, 6, 8, or 10 digits (split evenly)

  const match = mgrs.match(/^(\d{1,2})([A-Z])([A-Z]{2})(\d*)$/);
  if (!match) {
    // Try without square ID (GZD only — unlikely but handle gracefully)
    const gzdMatch = mgrs.match(/^(\d{1,2})([A-Z])([A-Z]{0,2})$/);
    if (gzdMatch) {
      return {
        gzd: `${gzdMatch[1]}${gzdMatch[2]}`,
        squareId: gzdMatch[3] || '',
        eastingStr: '',
        northingStr: '',
      };
    }
    return { gzd: mgrs, squareId: '', eastingStr: '', northingStr: '' };
  }

  const zoneNum = match[1];
  const bandLetter = match[2];
  const squareId = match[3];
  const digits = match[4];

  const half = digits.length / 2;
  const eastingStr = digits.slice(0, half);
  const northingStr = digits.slice(half);

  return {
    gzd: `${zoneNum}${bandLetter}`,
    squareId,
    eastingStr,
    northingStr,
  };
}

/**
 * Decompose an MGRS string into numeric UTM components.
 *
 * @param {string} mgrs - Full-precision MGRS string (precision 5)
 * @returns {{ zone: number, letter: string, easting: number, northing: number }}
 */
function decomposeMgrs(mgrs) {
  // Use a round-trip through the library to get accurate UTM coordinates.
  // Parse the GZD from the string for zone/letter info.
  const gzdMatch = mgrs.match(/^(\d{1,2})([A-Z])/);
  const zone = parseInt(gzdMatch[1], 10);
  const letter = gzdMatch[2];

  // Get the lat/lng from the MGRS string
  const point = toPoint(mgrs);
  const lng = point[0];
  const lat = point[1];

  // Compute UTM easting/northing from the MGRS numerical part.
  // The MGRS 100km square ID encodes the column/row within the zone.
  // We reconstruct easting and northing from the full MGRS decomposition.
  const { squareId, eastingStr, northingStr } = decomposeMgrsRaw(mgrs);

  // Convert the 100km square letters to easting/northing offsets
  const colLetter = squareId[0];
  const rowLetter = squareId[1];

  // Column letter encodes easting: each letter = 100km
  // The column letter set repeats every 3 zones (sets 1-3)
  const set = ((zone - 1) % 3);
  const colOrigins = ['ABCDEFGH', 'JKLMNPQR', 'STUVWXYZ'];
  const colIndex = colOrigins[set].indexOf(colLetter);
  const easting100km = (colIndex + 1) * 100000; // 1-indexed (100000 to 800000)

  // For a 5-digit precision the eastingStr is 5 digits representing meters
  // within the 100km square
  const precision = eastingStr.length;
  const scale = Math.pow(10, 5 - precision);
  const eastingOffset = parseInt(eastingStr || '0', 10) * scale;
  const northingOffset = parseInt(northingStr || '0', 10) * scale;

  const easting = easting100km + eastingOffset;

  // Row letter encodes northing within a 2,000,000m cycle
  // The row letter set alternates every zone (odd/even)
  const rowSets = [
    'ABCDEFGHJKLMNPQRSTUV', // odd zones
    'FGHJKLMNPQRSTUVABCDE', // even zones
  ];
  const rowSet = rowSets[zone % 2 === 0 ? 1 : 0];
  const rowIndex = rowSet.indexOf(rowLetter);
  const northing100km = rowIndex * 100000;

  // Determine the base northing from the latitude band
  // We use the latitude to figure out which 2,000,000m cycle we're in
  const approxNorthing = latToApproxNorthing(lat);
  const cycleLength = 2000000;
  const baseCycle = Math.floor(approxNorthing / cycleLength) * cycleLength;
  let northing = baseCycle + northing100km + northingOffset;

  // Adjust if we overshot or undershot
  if (northing - approxNorthing > cycleLength / 2) {
    northing -= cycleLength;
  } else if (approxNorthing - northing > cycleLength / 2) {
    northing += cycleLength;
  }

  return { zone, letter, easting, northing };
}

/**
 * Approximate UTM northing from latitude (for cycle determination).
 *
 * @param {number} lat - Latitude in decimal degrees
 * @returns {number} Approximate northing in meters
 */
function latToApproxNorthing(lat) {
  // In the northern hemisphere, northing ≈ lat * 111,000
  // In the southern hemisphere, northing = 10,000,000 + (lat * 111,000)
  const metersPerDegree = 111320;
  if (lat >= 0) {
    return lat * metersPerDegree;
  }
  return 10000000 + lat * metersPerDegree;
}
